import { reverse } from "@reactivated";

import {
    EstimatedPaymentResponse,
    FinancingApplicationSubmitData,
    FinancingCreditAppSubmitResult,
    FinancingPlans,
    FinancingPreQualRequest,
    FinancingPreQualRequestResponse,
    FinancingPreQualResponse,
    MaybeFinancingAccountInquiry,
    PreQualCustomerResponse,
} from "../models/financing";
import { IProductID, isoProductID } from "../models/nominals";
import { check } from "../models/utils";
import { CSRF_HEADER, fetchJson, getCSRFToken } from "../utils/ajax";
import { PromiseMutex } from "../utils/mutex";
import { getPageSetting } from "../utils/settings";

export const listFinancingPlans = async (): Promise<FinancingPlans> => {
    const mutex = new PromiseMutex<FinancingPlans>("list-financing-plans");
    let loading = mutex.getPromise();
    if (!loading) {
        loading = fetchJson(reverse("financingapi:plan-list")).then((body) => {
            return check(FinancingPlans.decode(body));
        });
        mutex.setPromise(loading);
    }
    return loading;
};

export const listFinancingPlansForProduct = async (
    productID: IProductID,
): Promise<FinancingPlans> => {
    const unwrappedProductID = isoProductID.unwrap(productID);
    const mutex = new PromiseMutex<FinancingPlans>(
        `list-financing-plans-for-product-${unwrappedProductID}`,
    );
    let loading = mutex.getPromise();
    if (!loading) {
        loading = fetchJson(
            reverse("product-financing-plans", { pk: unwrappedProductID }),
        ).then((body) => {
            return check(FinancingPlans.decode(body));
        });
        mutex.setPromise(loading);
    }
    return loading;
};

export const getEstimatedMonthlyPrice = async (
    principal: string,
): Promise<EstimatedPaymentResponse> => {
    const params = new URLSearchParams({ price: principal });
    const body = await fetchJson(
        `${reverse("financingapi:estimated-payment")}?${params}`,
    );
    return check(EstimatedPaymentResponse.decode(body));
};

export const submitFinancingCreditApp = async (
    data: FinancingApplicationSubmitData,
    recaptcha?: string | null,
): Promise<FinancingCreditAppSubmitResult> => {
    const body = await fetchJson(reverse("financingapi:apply"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            [CSRF_HEADER]: await getCSRFToken(),
        },
        body: JSON.stringify({
            ...data,
            recaptcha: recaptcha ?? undefined,
        }),
    });
    return check(FinancingCreditAppSubmitResult.decode(body));
};

export const updatePendingCreditAppStatus =
    async (): Promise<MaybeFinancingAccountInquiry> => {
        const body = await fetchJson(reverse("financingapi:update-inquiry"), {
            method: "POST",
            headers: {
                [CSRF_HEADER]: await getCSRFToken(),
            },
        });
        return check(MaybeFinancingAccountInquiry.decode(body || null));
    };

export const checkFinancingPreQualStatus = async (
    data: FinancingPreQualRequest,
    recaptcha?: string | null,
): Promise<FinancingPreQualRequestResponse> => {
    const body = await fetchJson(reverse("financingapi:prequal"), {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            [CSRF_HEADER]: await getCSRFToken(),
        },
        body: JSON.stringify({
            ...data,
            recaptcha: recaptcha ?? undefined,
        }),
    });
    return check(FinancingPreQualRequestResponse.decode(body));
};

export const setPreQualCustomerResponse = async (
    customerResp: PreQualCustomerResponse,
): Promise<FinancingPreQualResponse> => {
    const data = {
        customer_response: customerResp,
    };
    const body = await fetchJson(
        reverse("financingapi:prequal-customer-response"),
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                [CSRF_HEADER]: await getCSRFToken(),
            },
            body: JSON.stringify(data),
        },
    );
    return check(FinancingPreQualResponse.decode(body));
};

export const sendPreScreenOfferDisplayConfirmation = async (
    uniqueId: string,
): Promise<void> => {
    // obtain the API endpoint from page settings
    const apiURL = getPageSetting("wfrs_prescreen_api");
    if (!apiURL) {
        throw new Error("WFRS prescreen API URL is not configured");
    }
    // Use direct fetch() instead of fetchJson() because this external WFRS API
    // expects application/x-www-form-urlencoded, not JSON
    // According to the docs (as of 2020-01-29), it really is `offerDisplyed`, not `offerDisplayed`
    const data = `uniqueId=${uniqueId}&offerDisplyed=Y`;
    await fetch(apiURL, {
        method: "POST",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
        },
        body: data,
        credentials: "omit", // External API - don't send cookies
    });
};

export const updatePreScreenChoiceValue = async (
    choice: PreQualCustomerResponse,
    uniqueId: string,
): Promise<void> => {
    // obtain the API endpoint from page settings
    const apiURL = getPageSetting("wfrs_prescreen_api");
    if (!apiURL) {
        throw new Error("WFRS prescreen API URL is not configured");
    }

    // use remind_me as default response
    let buttonId = "remind_me";
    if (choice === PreQualCustomerResponse.ACCEPT) {
        buttonId = "apply_now";
    }

    // Use direct fetch() instead of fetchJson() because this external WFRS API
    // expects application/x-www-form-urlencoded, not JSON
    await fetch(apiURL, {
        method: "POST",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
        },
        body: `uniqueId=${uniqueId}&buttonId=${buttonId}`,
        credentials: "omit", // External API - don't send cookies
    });
};
